import { useMemo } from "react";
import { ShiftType } from "@/types/db/shifts";
import StackNavigator, { Stack } from "./StackNavigator";
import PositionsScreen from "./screens/PositionsScreen";
import AddUsersScreen from "./screens/AddUsersScreen";
import AssignedUsersScreen from "./screens/AssignedUsersScreen";
import { PositionsProvider } from "./PositionsContext";
import { Position } from "./PositionCard";

export interface ShiftPositionsNewProps {
  shift: ShiftType | null;
  loading?: boolean;
}

export function ShiftPositionsNew({ shift, loading }: ShiftPositionsNewProps) {
  const positions = useMemo(() => {
    if (!shift?.shift_positions) return [];

    return shift.shift_positions.map(
      (sp): Position => ({
        shift_position_id: sp.id,
        position_id: sp.position_id,
        name: sp.position?.name || "Position",
        description: sp.position?.description,
        quantity: sp.quantity || 1,
        preferred_quantity: (sp as any).preferred_quantity,
        gifted_hours: (sp as any).gifted_hours,
        supervisory: sp.supervisor,
        invited_users: (sp.shift_users || []).map((su) => ({
          id: su.id,
          status: su.status || "QUEUED",
          meta_data: (su as any).meta_data,
          user: su.user,
        })),
        location_override: sp.location_id ? { location: sp.location } : undefined,
        location: sp.location,
        dress_code: (sp as any).dress_code,
        attachments: (sp as any).attachments || [],
      }),
    );
  }, [shift]);

  return (
    <PositionsProvider
      positions={positions}
      loading={loading}
      shiftId={shift?.id}
      shiftLocation={shift?.location}
      projectLocation={shift?.project?.location}
      shiftDressCode={shift?.dress_code}
      projectDressCode={shift?.project?.dress_code}
      shiftMarkets={shift?.meta_data?.markets}
      projectMarkets={(shift?.project as any)?.meta_data?.markets}
      clientAbilities={(shift?.project as any)?.client?.client_abilities?.map((a: any) => a.ability)}
      projectAbilities={(shift?.project as any)?.project_abilities?.map((a: any) => a.ability)}
      shiftAbilities={(shift as any)?.shift_abilities?.map((a: any) => a.ability)}
      clientRestrictions={(shift?.project as any)?.client?.client_restrictions?.map((r: any) => r.restriction)}
      projectRestrictions={(shift?.project as any)?.project_restrictions?.map((r: any) => r.restriction)}
      shiftRestrictions={(shift as any)?.shift_restrictions?.map((r: any) => r.restriction)}
    >
      <StackNavigator initialRouteName="Positions">
        <Stack.Screen name="Positions" component={PositionsScreen} options={{ title: "Positions" }} />
        <Stack.Screen name="AddUsers" component={AddUsersScreen} options={{ title: "Add Users" }} />
        <Stack.Screen name="AssignedUsers" component={AssignedUsersScreen} options={{ title: "Assigned Users" }} />
      </StackNavigator>
    </PositionsProvider>
  );
}
